import { TreeNodeLike } from '../Types/Data.type';

export type Option = { value: string; label: string };


//ordinamento
export const sortByOrdine = <T extends { ordine?: number }>(a: T, b: T) =>
  (a.ordine ?? 0) - (b.ordine ?? 0);


/**
 * Ricostruisce l'albero se l'array è piatto (senza children)
 * oppure lo normalizza (assicurando children[] e ordinamenti).
 * Considera root sia parentID null che 0.
 */

type WithChildren = TreeNodeLike & { children: TreeNodeLike[] };
export function ensureTree(input: TreeNodeLike[]): TreeNodeLike[] {
  if (!Array.isArray(input) || input.length === 0) return [];

  const hasChildrenAlready = input.some(n => Array.isArray(n.children) && n.children.length > 0);
  if (hasChildrenAlready) {
    // Normalizza/ordina mantenendo children sempre come array
    const normalize = (nodes: TreeNodeLike[]): TreeNodeLike[] =>
      nodes
        .slice()
        .sort(sortByOrdine)
        .map(n => ({
          ...n,
          children: normalize(n.children ?? []),
        }));
    // Roots: parentID null o 0
    return normalize(input.filter(n => n.parentID === null || n.parentID === 0));
  }

  // Ricostruisco da lista piatta
  const byId = new Map<string, WithChildren>();
  input.forEach(n => {
    byId.set(String(n.ID), { ...n, children: [] });
  });

  const roots: WithChildren[] = [];
  byId.forEach(node => {
    const p = node.parentID;
    if (p === null || p === 0 || !byId.has(String(p))) {
      roots.push(node);
    } else {
      byId.get(String(p))!.children.push(node);
    }
  });

  // Accetta anche nodi senza children definiti e usa fallback []
  const sortDeep = (nodes: TreeNodeLike[]): TreeNodeLike[] =>
    nodes
      .slice()
      .sort(sortByOrdine)
      .map(n => ({
        ...n,
        children: sortDeep((n as any).children ?? []),
      }));

  return sortDeep(roots);
}



/**
 * Crea le opzioni da usare in <select>, con indentazione visuale.
 * - excludeId: evita che un nodo diventi padre di sé stesso (utile in edit)
 * - indent: stringa per l'indentazione (NBSP di default)
 * - getLabel: funzione per generare l'etichetta
 */
export function buildTreeOptions(
  list: TreeNodeLike[], { excludeId, indent = '\u00A0\u00A0', getLabel, }: 
  { excludeId?: number | string; indent?: string; getLabel?: (n: TreeNodeLike) => string; } = {}
): Option[] {
  const roots = ensureTree(list);
  const opts: Option[] = [];

  const labelFn =
    getLabel ??
    ((n: TreeNodeLike) => {
      const base = n.nome_it ?? n.nome ?? `#${n.ID}`;
      return base;
    });

  const visit = (node: TreeNodeLike, depth: number) => {
    if (excludeId && String(node.ID) === String(excludeId)) return;
    opts.push({
      value: String(node.ID),
      label: `${indent.repeat(depth)}${labelFn(node)}`,
    });
    (node.children ?? []).forEach(child => visit(child, depth + 1));
  };

  roots.forEach(r => visit(r, 0));
  return opts;
}




//Funzione per prendere il meta dalle categorie
export function parseMetaName(meta: unknown, lang = 'it'): string {
    if (!meta) return '';
    try {
        const obj = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
        const byLang = obj?.[lang] || {};
        return (byLang.nome ?? '').toString();
    } catch {
        return '';
    }
}



//funzione per formattare la data in italiano :
// es-> 2025-12-05 07:00:03 in 05/12/2025 07:00:03 
export const formatDateIT = ( dateInput: string | Date, withTime: boolean = true): string => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime && {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };

  return new Intl.DateTimeFormat("it-IT", options).format(date);
};
